import { zodResolver } from "@hookform/resolvers/zod";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import * as z from "zod";
import Container from "@src/components/shared/Container";
import TitleAndDesc from "@src/components/shared/TitleAndDesc";
import { Button } from "@src/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
} from "@src/components/ui/form";
import { Input } from "@src/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@src/components/ui/select";
import { getProp, getMaxDateForBirth } from "@src/helpers";
import {
  GEO_LAT_LETTERS_ONLY,
  LATIN_LETTERS_WITH_NUMBERS,
  DIGIT_SPACE_DASH_LATIN_GEORGIAN_ONLY_REGEX,
} from "@src/helpers/regex";
import useZustandStore from "@src/helpers/zustand";

type TInputs =
  | "name"
  | "lastname"
  | "id"
  | "docNumber"
  | "sex"
  | "issuringAuthority";

function ReviewDetails() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { data: globalState, setData } = useZustandStore((state) => state);
  const FormSchema = z
    .object({
      name: z.string().refine((val) => val, { message: "სავალდებულო ველი" }),
      lastname: z
        .string()
        .refine((val) => val, { message: "სავალდებულო ველი" }),
      id: z.string().refine((val) => val, { message: "სავალდებულო ველი" }),
      docNumber: z
        .string()
        .refine((val) => val, { message: "სავალდებულო ველი" }),
      sex: z.string().refine((val) => val, { message: "სავალდებულო ველი" }),
      issueDate: z.date().refine((val) => val, { message: "სავალდებულო ველი" }),
      expiryDate: z
        .date()
        .refine((val) => val, { message: "სავალდებულო ველი" }),
      issuringAuthority: z
        .string()
        .refine((val) => val, { message: "სავალდებულო ველი" }),
      birthDate: z.date().refine((val) => val, { message: "სავალდებულო ველი" }),
      documentType: z.string().optional(),
      documentFrontSide: z.string().optional(),
      documentBackSide: z.string().optional(),
      selfImage: z.string().optional(),
    })
    .superRefine((data, ctx) => {
      if (globalState.isManual) {
        if (!data.documentType) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "სავალდებულო ველი",
            path: ["documentType"],
          });
        }
        if (!data.documentFrontSide) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "სავალდებულო ველი",
            path: ["documentFrontSide"],
          });
        }
        if (!data.documentBackSide) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "სავალდებულო ველი",
            path: ["documentBackSide"],
          });
        }
        if (!data.selfImage) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "სავალდებულო ველი",
            path: ["selfImage"],
          });
        }
      }
    });
  const { inputFields, defaultValues } = useMemo(() => {
    const tmpFields = [
      "name",
      "lastname",
      "id",
      "docNumber",
      "issuringAuthority",
    ];

    return {
      inputFields: tmpFields,
      defaultValues: {
        name: globalState.closeSession.firstName,
        lastname: globalState.closeSession.lastName,
        id: globalState.closeSession.personalNumber,
        docNumber: globalState.closeSession.documentNumber,
        sex: globalState.closeSession.sex,
        issueDate: getProp(
          () => new Date(globalState.closeSession.issueDate),
          new Date()
        ),
        expiryDate: getProp(
          () => new Date(globalState.closeSession.expirationDate),
          new Date()
        ),
        birthDate: getProp(
          () => new Date(globalState.closeSession.birthDate),
          new Date()
        ),
        issuringAuthority: globalState.closeSession.authority,
        ...(globalState.isManual && {
          documentType: globalState.closeSession.documetType,
          documentFrontSide: globalState.closeSession.documentFrontSide,
          documentBackSide: globalState.closeSession.documentBackSide,
          selfImage: globalState.closeSession.selfImage,
        }),
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues,
  });

  function onSubmit(data: z.infer<typeof FormSchema>) {
    setData({
      ...globalState,
      closeSession: {
        ...globalState.closeSession,
        firstName: data.name,
        lastName: data.lastname,
        personalNumber: data.id,
        documentNumber: data.docNumber,
        sex: data.sex,
        issueDate: format(data.issueDate, "yyyy-MM-dd"),
        expirationDate: format(data.expiryDate, "yyyy-MM-dd"),
        authority: data.issuringAuthority,
        birthDate: format(data.birthDate, "yyyy-MM-dd"),
        ...(globalState.isManual && {
          documetType: data.documentType,
          documentFrontSide: data.documentFrontSide,
          documentBackSide: data.documentBackSide,
          selfImage: data.selfImage,
        }),
      },
    });
    navigate(`/${i18n.language}/citizenship`);
  }

  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    setter: (val: string) => void
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === "string") {
        setter(reader.result.split(",")[1] ?? "");
      }
    };
    reader.readAsDataURL(file);
  };

  console.log("Form errors:", form.getValues());

  return (
    <Container className="max-sm:bg-white">
      <Form {...form}>
        <div className="py-4">
          <TitleAndDesc
            desc={t("reviewDetails.desc")}
            title={t("reviewDetails.title")}
          />
        </div>
        <form
          className="flex flex-col gap-3 max-md:mb-40"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          {/* name */}
          {inputFields.map((el) => {
            return (
              <FormField
                control={form.control}
                key={el}
                name={el as TInputs}
                render={({ field }) => {
                  return (
                    <FormItem>
                      <FormControl>
                        <Input
                          {...field}
                          label={t(`reviewDetails.inputs.${field.name}`)}
                          onChange={(e) => {
                            const { target } = e;
                            if (
                              (el === "issuringAuthority" &&
                                DIGIT_SPACE_DASH_LATIN_GEORGIAN_ONLY_REGEX.test(
                                  target.value
                                )) ||
                              !target.value
                            ) {
                              field.onChange(e);
                            } else if (
                              (["name", "lastname"].includes(el) &&
                                GEO_LAT_LETTERS_ONLY.test(target.value)) ||
                              !target.value
                            ) {
                              field.onChange(e);
                            } else if (
                              LATIN_LETTERS_WITH_NUMBERS.test(target.value) ||
                              !target.value
                            ) {
                              field.onChange(e);
                            }
                          }}
                        />
                      </FormControl>
                    </FormItem>
                  );
                }}
              />
            );
          })}

          <div className="relative mt-1 w-full [&>div]:w-full">
            <FormField
              control={form.control}
              name="birthDate"
              render={({ field }) => {
                return (
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      format="dd/MM/yyyy"
                      label={t("reviewDetails.inputs.birthDate")}
                      maxDate={getMaxDateForBirth()}
                      onChange={field.onChange}
                      slotProps={{
                        layout: {
                          sx: {
                            "& .Mui-selected": {
                              backgroundColor: "#eabe4d !important",
                              color: "#1b1d1f !important",
                              fontWeight: 500,
                            },
                          },
                        },
                      }}
                      slots={{
                        // eslint-disable-next-line react/no-unstable-nested-components
                        openPickerIcon: () => (
                          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                        ),
                      }}
                      sx={{
                        "& .MuiInputLabel-shrink": {
                          position: "absolute",
                          top: "8px",
                        },
                        "& .MuiInputBase-root": {
                          paddingRight: "24px",
                          borderRadius: "8px",
                          "&:hover": {
                            "& fieldset": {
                              borderColor: "rgba(39, 44, 69, 0.20)",
                            },
                          },
                        },
                        "& fieldset": {
                          borderColor: "rgba(39, 44, 69, 0.20)",
                        },
                        "& legend": {
                          display: "none",
                        },
                      }}
                      value={field.value}
                    />
                  </LocalizationProvider>
                );
              }}
            />
          </div>

          <div className="relative mt-1 w-full [&>div]:w-full">
            <FormField
              control={form.control}
              name="issueDate"
              render={({ field }) => {
                return (
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      format="dd/MM/yyyy"
                      label={t("reviewDetails.inputs.issueDate")}
                      onChange={field.onChange}
                      slotProps={{
                        layout: {
                          sx: {
                            "& .Mui-selected": {
                              backgroundColor: "#eabe4d !important",
                              color: "#1b1d1f !important",
                              fontWeight: 500,
                            },
                          },
                        },
                      }}
                      slots={{
                        // eslint-disable-next-line react/no-unstable-nested-components
                        openPickerIcon: () => (
                          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                        ),
                      }}
                      sx={{
                        "& .MuiInputLabel-shrink": {
                          position: "absolute",
                          top: "8px",
                        },
                        "& .MuiInputBase-root": {
                          paddingRight: "24px",
                          borderRadius: "8px",
                          "&:hover": {
                            "& fieldset": {
                              borderColor: "rgba(39, 44, 69, 0.20)",
                            },
                          },
                        },
                        "& fieldset": {
                          borderColor: "rgba(39, 44, 69, 0.20)",
                        },
                        "& legend": {
                          display: "none",
                        },
                      }}
                      value={field.value}
                    />
                  </LocalizationProvider>
                );
              }}
            />
          </div>
          <div className="relative mt-1 w-full [&>div]:w-full">
            <FormField
              control={form.control}
              name="expiryDate"
              render={({ field }) => {
                return (
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      format="dd/MM/yyyy"
                      label={t("reviewDetails.inputs.expiryDate")}
                      onChange={field.onChange}
                      slotProps={{
                        layout: {
                          sx: {
                            "& .Mui-selected": {
                              backgroundColor: "#eabe4d !important",
                              color: "#1b1d1f !important",
                              fontWeight: 500,
                            },
                          },
                        },
                      }}
                      slots={{
                        // eslint-disable-next-line react/no-unstable-nested-components
                        openPickerIcon: () => (
                          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                        ),
                      }}
                      sx={{
                        "& .MuiInputLabel-shrink": {
                          position: "absolute",
                          top: "8px",
                        },

                        "& fieldset": {
                          borderColor: "rgba(39, 44, 69, 0.20)",
                        },
                        "& .MuiInputBase-root": {
                          paddingRight: "24px",
                          borderRadius: "8px",
                          "&:hover": {
                            "& fieldset": {
                              borderColor: "rgba(39, 44, 69, 0.20)",
                            },
                          },
                        },
                        "& legend": {
                          display: "none",
                        },
                      }}
                      value={field.value}
                    />
                  </LocalizationProvider>
                );
              }}
            />
            <div className="mt-2">
              <FormField
                control={form.control}
                name="sex"
                render={({ field }) => {
                  return (
                    <Select onValueChange={field.onChange} value={field.value}>
                      <SelectTrigger
                        className="w-full text-base"
                        placeholder={t("reviewDetails.inputs.sex")}
                      >
                        <SelectValue
                          placeholder={t("reviewDetails.inputs.sex")}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">
                          {t("reviewDetails.male")}
                        </SelectItem>
                        <SelectItem value="female">
                          {t("reviewDetails.female")}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  );
                }}
              />
            </div>
          </div>

          {globalState.isManual && (
            <>
              <div className="mt-4">
                <FormField
                  control={form.control}
                  name="documentType"
                  render={({ field }) => (
                    <Select
                      value={field.value}
                      onValueChange={(val) => {
                        field.onChange(val);
                      }}
                    >
                      <SelectTrigger
                        className="w-full text-base"
                        placeholder={t("reviewDetails.inputs.documentType")}
                      >
                        <SelectValue
                        // placeholder={t("reviewDetails.inputs.documentType")}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ID">
                          {t("reviewDetails.inputs.idCard")}
                        </SelectItem>
                        <SelectItem value="PASSPORT">
                          {t("reviewDetails.inputs.passport")}
                        </SelectItem>
                        <SelectItem value="ResidencePermit">
                          {t("reviewDetails.inputs.residencePermit")}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
              <div className="mt-4">
                <label className="block mb-2">
                  {t("reviewDetails.inputs.documentFrontSide")}
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) =>
                    handleFileChange(e, (val) => {
                      form.setValue("documentFrontSide", val, {
                        shouldValidate: true,
                        shouldDirty: true,
                      });
                    })
                  }
                />
                {form.formState.errors.documentFrontSide && (
                  <span className="text-red-500">
                    {form.formState.errors.documentFrontSide.message}
                  </span>
                )}
              </div>
              <div className="mt-4">
                <label className="block mb-2">
                  {t("reviewDetails.inputs.documentBackSide")}
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) =>
                    handleFileChange(e, (val) => {
                      form.setValue("documentBackSide", val, {
                        shouldValidate: true,
                        shouldDirty: true,
                      });
                    })
                  }
                />
              </div>
              <div className="mt-4">
                <label className="block mb-2">
                  {t("reviewDetails.inputs.selfImage")}
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) =>
                    handleFileChange(e, (val) => {
                      form.setValue("selfImage", val, {
                        shouldValidate: true,
                        shouldDirty: true,
                      });
                    })
                  }
                />
              </div>
            </>
          )}

          <div className="z-10 max-md:fixed max-md:bottom-0 max-md:left-0 max-md:right-0">
            <div className="rounded-6 w-full self-end bg-white py-8 max-md:px-4 ">
              <Button
                className="w-full"
                disabled={!form.formState.isValid}
                type="submit"
              >
                {t("continue")}
              </Button>
            </div>
          </div>
        </form>
      </Form>
    </Container>
  );
}

export default ReviewDetails;
